import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  motion,
  useScroll,
  useTransform,
  AnimatePresence,
} from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";

const EASE = [0.22, 1, 0.36, 1];

// ----- helper: masked line reveal -----
function LineMask({ children, delay = 0, className = "" }) {
  return (
    <span className={`line-mask ${className}`}>
      <motion.span
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1.05, delay, ease: EASE }}
        style={{ display: "inline-block" }}
      >
        {children}
      </motion.span>
    </span>
  );
}

// Warm, classy fabric fallbacks — used if admin hasn't uploaded any banners.
// Tones chosen to harmonise with ivory bg + deep navy text + gold accent.
const FABRIC_FALLBACKS = [
  {
    id: "fab-1",
    title: "Champagne silk",
    subtitle: "Warm neutral drapery",
    image_base64:
      "https://images.unsplash.com/photo-1606603050316-9caf9ea5e8d9?auto=format&fit=crop&w=1800&q=85",
  },
  {
    id: "fab-2",
    title: "Ivory brocade",
    subtitle: "Editorial soft-focus fabric",
    image_base64:
      "https://images.unsplash.com/photo-1519643381401-22c77e60520e?auto=format&fit=crop&w=1800&q=85",
  },
  {
    id: "fab-3",
    title: "Golden weave",
    subtitle: "Textile close-up in warm light",
    image_base64:
      "https://images.unsplash.com/photo-1620799140408-edc6dcb6d633?auto=format&fit=crop&w=1800&q=85",
  },
  {
    id: "fab-4",
    title: "Blush dupioni",
    subtitle: "Soft rose silk — a nod to the flame accent",
    image_base64:
      "https://images.unsplash.com/photo-1594736797933-d0501ba2fe65?auto=format&fit=crop&w=1800&q=85",
  },
];

// ----- HERO with banner carousel + parallax -----
function Hero({ banners }) {
  const ref = useRef(null);
  const [idx, setIdx] = useState(0);
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 800], [0, 160]);
  const scale = useTransform(scrollY, [0, 800], [1, 1.12]);
  const opacity = useTransform(scrollY, [0, 500], [1, 0.15]);

  // Prefer admin-managed banners; fall back to warm fabric photos if none.
  const slides = (banners && banners.length ? banners : FABRIC_FALLBACKS);

  useEffect(() => {
    if (!slides?.length) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % slides.length), 5500);
    return () => clearInterval(t);
  }, [slides]);

  const current = slides?.[idx];

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden bg-ivory"
    >
      {/* parallax background carousel */}
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <AnimatePresence mode="sync">
          {current && (
            <motion.div
              key={current.id}
              initial={{ opacity: 0, scale: 1.06 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.02 }}
              transition={{
                opacity: { duration: 1.8, ease: "easeInOut" },
                scale: { duration: 6, ease: "easeOut" },
              }}
              className="absolute inset-0"
              data-testid={`hero-slide-${idx}`}
            >
              <img
                src={current.image_base64}
                alt={current.title}
                className="w-full h-full object-cover opacity-55 md:opacity-60"
              />
              {/* Base ivory scrim — softens fabric top & bottom */}
              <div className="absolute inset-0 bg-gradient-to-b from-ivory/70 via-ivory/30 to-ivory/85" />
              <div className="absolute inset-0 bg-gradient-to-r from-ivory/50 via-transparent to-ivory/50" />
              {/* Text-safe halo: a soft ivory light centered on the hero content */}
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_55%_at_50%_48%,rgba(246,242,234,0.75)_0%,rgba(246,242,234,0.35)_45%,transparent_75%)]" />
              {/* Corner vignette so edges blend into the page */}
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,transparent_55%,rgba(246,242,234,0.85)_100%)]" />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* spotlight glow */}
      <div className="absolute inset-0 spotlight pointer-events-none" />

      {/* content — centered */}
      <motion.div
        style={{ opacity }}
        className="relative z-10 mx-auto max-w-[1400px] px-5 md:px-10 pt-28 md:pt-36 pb-20 md:pb-28 min-h-[100svh] flex flex-col items-center text-center justify-between"
      >
        <div className="w-full flex flex-col items-center">
          {/* B'Premium logo, centered above headline */}
          <motion.img
            src="https://customer-assets-cm19k8pv.emergentagent.net/job_bpremium-shop/artifacts/mvmypmho_1.png"
            alt="B'Premium"
            data-testid="hero-brand-logo"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE }}
            className="h-28 sm:h-36 md:h-52 lg:h-64 w-auto mb-0 select-none"
            draggable={false}
          />
          <motion.p
            data-testid="hero-unit-tag"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.35, ease: EASE }}
            className="font-mono text-[10px] sm:text-[11px] tracking-[0.35em] uppercase text-navy/80 -mt-7 md:-mt-10 mb-7 md:mb-10"
          >
            A unit of Babu Tex
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="w-10 h-px bg-gold" />
            <span className="chapter-num">Chapter 01 — Est. 2025</span>
            <span className="w-10 h-px bg-gold" />
          </motion.div>

          <h1 className="font-display font-light text-navy text-[10vw] sm:text-[8.5vw] md:text-[7.2vw] lg:text-[6.3vw] leading-[0.98] tracking-tight max-w-[20ch] mx-auto">
            <LineMask delay={0.15}>Woven&nbsp;</LineMask>
            <LineMask delay={0.3}>
              <span className="italic text-gold">with</span>
            </LineMask>
            <br />
            <LineMask delay={0.45}>intent, worn</LineMask>
            <br />
            <LineMask delay={0.6}>with meaning.</LineMask>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.95, ease: EASE }}
            className="mt-8 md:mt-10 max-w-lg text-navy/80 text-base md:text-lg font-light leading-relaxed"
          >
            A house of premium fabrics — from heritage silks to modern
            brocades, crafted for those who dress with intention.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1, ease: EASE }}
            className="mt-10 flex flex-wrap items-center justify-center gap-3"
          >
            <Link
              to="/catalog"
              data-testid="hero-explore-btn"
              className="group inline-flex items-center gap-2 bg-navy text-ivory px-7 py-3.5 rounded-full text-xs tracking-[0.2em] uppercase font-bold hover:bg-navy-800 transition-colors"
            >
              Explore Collection
              <ArrowRight
                size={16}
                className="transition-transform group-hover:translate-x-0.5"
              />
            </Link>
            <a
              href="#story"
              data-testid="hero-story-btn"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-navy/25 text-navy/85 text-xs tracking-[0.2em] uppercase hover:border-navy hover:text-navy transition-colors"
            >
              Our story
            </a>
          </motion.div>
        </div>

        {/* bottom info row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.25, ease: EASE }}
          className="hidden md:flex w-full items-end justify-between text-xs text-navy/60 mt-16"
        >
          <div className="max-w-xs text-left">
            <p className="chapter-num mb-2">Now showing</p>
            <p className="font-display text-navy text-xl italic">
              {current?.title || "Premium Collection"}
            </p>
            {current?.subtitle && (
              <p className="mt-1 text-navy/50">{current.subtitle}</p>
            )}
          </div>

          <div className="flex items-center gap-4">
            {slides?.map((_, i) => (
              <button
                key={i}
                data-testid={`hero-dot-${i}`}
                onClick={() => setIdx(i)}
                className={`h-px transition-all duration-500 ${
                  i === idx ? "w-14 bg-gold" : "w-8 bg-navy/30 hover:bg-navy/70"
                }`}
                aria-label={`Slide ${i + 1}`}
              />
            ))}
          </div>

          <div className="text-right">
            <p className="chapter-num mb-2">Scroll</p>
            <p className="font-display italic text-navy">↓</p>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}

// ----- Marquee -----
function Marquee() {
  // trailing spaces + bullet ensure the loop chains seamlessly with no visible gap
  const text =
    "Premium Fabric Collections  •  B'Premium  •  Woven Heritage  •  Editorial Textiles  •  ";
  return (
    <section
      data-testid="editorial-marquee"
      className="relative overflow-hidden border-y border-navy/10 bg-navy py-10 md:py-16"
    >
      <div className="marquee-track flex whitespace-nowrap will-change-transform">
        <span className="marquee-text text-ivory">{text.repeat(4)}</span>
        <span className="marquee-text text-ivory" aria-hidden>
          {text.repeat(4)}
        </span>
      </div>
    </section>
  );
}

// ----- Manifesto numbered chapters -----
function Manifesto() {
  const chapters = [
    {
      n: "01",
      title: "Heritage threads",
      body: "We source only from mills we trust. Every weave carries a lineage — from the loom to your closet.",
    },
    {
      n: "02",
      title: "Considered palette",
      body: "Deep navies, ivory neutrals, gilded golds. Colour that whispers, never shouts.",
    },
    {
      n: "03",
      title: "Craft, not catalogue",
      body: "Small runs. Signed by hand. Numbered. Because what you wear should feel like it was made for you.",
    },
    {
      n: "04",
      title: "Modern editorial",
      body: "Traditional craft, contemporary drapes. A house that reads the room and writes its own line.",
    },
  ];

  return (
    <section
      id="story"
      data-testid="manifesto-section"
      className="relative bg-ivory-200 py-24 md:py-40"
    >
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="grid md:grid-cols-12 gap-10 md:gap-20 mb-16 md:mb-24">
          <div className="md:col-span-4">
            <p className="chapter-num mb-4">— The manifesto</p>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1, ease: EASE }}
              className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-navy"
            >
              Four <span className="italic text-gold">principles.</span>
              <br />
              One house.
            </motion.h2>
          </div>
          <div className="md:col-span-7 md:col-start-6">
            <p className="text-navy/65 text-base md:text-lg font-light leading-relaxed max-w-xl">
              B'Premium exists at the intersection of heritage and modernity —
              where every metre of fabric is a statement of intent, and every
              collection is a chapter in a longer story.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-x-16 gap-y-14 md:gap-y-20">
          {chapters.map((c, i) => (
            <motion.div
              key={c.n}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, delay: i * 0.08, ease: EASE }}
              className={`relative ${i % 2 ? "md:mt-16" : ""}`}
            >
              <div className="flex items-baseline gap-4 mb-4">
                <span className="font-mono text-gold text-sm">{c.n}</span>
                <span className="h-px flex-1 bg-navy/15" />
              </div>
              <h3 className="font-display text-2xl md:text-3xl text-navy mb-3">
                {c.title}
              </h3>
              <p className="text-navy/60 text-base font-light leading-relaxed max-w-md">
                {c.body}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ----- Featured products -----
function Featured({ products, settings }) {
  const list = (products || []).slice(0, 6);
  return (
    <section
      data-testid="featured-section"
      className="relative bg-ivory py-24 md:py-40 border-t border-navy/10"
    >
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-14 md:mb-20">
          <div>
            <p className="chapter-num mb-4">Our product</p>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: EASE }}
              className="font-display text-4xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-navy underline decoration-1 underline-offset-[10px] decoration-navy/30"
            >
              Explore our <br />
              <span className="italic text-gold">fabric collection.</span>
            </motion.h2>
          </div>
          <Link
            to="/catalog"
            data-testid="featured-view-all"
            className="group inline-flex items-center gap-2 self-start md:self-end text-xs tracking-[0.2em] uppercase text-navy/70 hover:text-gold transition-colors"
          >
            View entire catalog
            <ArrowUpRight
              size={14}
              className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            />
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-6">
          {list.map((p, i) => (
            <ProductCard
              key={p.id}
              product={p}
              index={i}
              whatsappNumber={settings?.whatsapp}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

// ----- Home page composition -----
export default function HomePage() {
  const [banners, setBanners] = useState([]);
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const [b, p, s] = await Promise.all([
          api.get("/banners"),
          api.get("/products"),
          api.get("/settings"),
        ]);
        setBanners((b.data || []).filter((x) => x.active !== false));
        setProducts((p.data || []).filter((x) => x.active !== false));
        setSettings(s.data || {});
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  return (
    <div data-testid="home-page">
      <Hero banners={banners} />
      <Marquee />
      <Manifesto />
      <Featured products={products} settings={settings} />
      {/* Footer is rendered by Layout */}
    </div>
  );
}

export { HomePage };
